import { useCallback, useEffect, useState } from "react";
import { Phone, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fileUrl } from "@/lib/api";

const STATUSES = ["new", "contacted", "scheduled", "completed", "archived"];
const FILTERS = ["all", "quote", "contact"];

const LeadCard = ({ l, onStatus, onDelete }) => (
  <div data-testid={`lead-${l.id}`} className="border border-white/10 bg-coal p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 font-mono text-[10.5px] uppercase tracking-[0.16em] ${l.kind === "quote" ? "bg-brand text-ink" : "bg-white/10 text-bone"}`}>{l.kind}</span>
          {l.urgency?.startsWith("Emergency") && <span className="bg-red-500/20 px-2 py-0.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-red-300">Emergency</span>}
          <span className="font-mono text-[11px] text-zinc-400">{new Date(l.created_at).toLocaleString()}</span>
        </div>
        <h3 className="font-display mt-2 text-xl font-extrabold uppercase">{l.name}</h3>
      </div>
      <div className="flex items-center gap-2">
        <Select value={l.status} onValueChange={(v) => onStatus(l.id, v)}>
          <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-9 w-36 rounded-none border-white/15 bg-transparent font-mono text-xs uppercase"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none border-white/10 bg-coal text-bone">{STATUSES.map((s) => <SelectItem key={s} value={s} className="uppercase">{s}</SelectItem>)}</SelectContent>
        </Select>
        <button data-testid={`lead-delete-${l.id}`} onClick={() => onDelete(l.id)} className="grid h-9 w-9 place-items-center border border-white/15 text-zinc-400 hover:border-red-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="mt-4 grid gap-x-6 gap-y-2 text-sm text-zinc-300 sm:grid-cols-2 lg:grid-cols-3">
      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 text-bone hover:text-brand"><Phone className="h-3.5 w-3.5 text-brand" />{l.phone}</a>
      {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 break-all hover:text-brand"><Mail className="h-3.5 w-3.5 text-brand" />{l.email}</a>}
      {l.service && <span><b className="text-zinc-400">Service:</b> {l.service}</span>}
      {(l.make || l.model || l.year) && <span><b className="text-zinc-400">Vehicle:</b> {[l.year, l.make, l.model].filter(Boolean).join(" ")}</span>}
      {l.location && <span><b className="text-zinc-400">Location:</b> {l.location}</span>}
      {l.urgency && <span><b className="text-zinc-400">When:</b> {l.urgency}</span>}
      {l.vehicle_type && <span><b className="text-zinc-400">Type:</b> {l.vehicle_type}</span>}
      {l.key_status && <span><b className="text-zinc-400">Key:</b> {l.key_status}</span>}
      {l.place && <span><b className="text-zinc-400">At:</b> {l.place}</span>}
      {(l.schedule_date || l.schedule_time) && <span><b className="text-zinc-400">Schedule:</b> {[l.schedule_date, l.schedule_time].filter(Boolean).join(" · ")}</span>}
      {l.preferred_contact && <span><b className="text-zinc-400">Contact by:</b> {l.preferred_contact}</span>}
      {l.vin && <span><b className="text-zinc-400">VIN:</b> {l.vin}</span>}
      {l.language && <span><b className="text-zinc-400">Language:</b> {l.language.toUpperCase()}</span>}
      {l.email_status && <span data-testid={`lead-email-status-${l.id}`}><b className="text-zinc-400">Email:</b> {l.email_status}</span>}
    </div>
    {l.message && <p className="mt-3 border-t border-white/10 pt-3 text-sm text-zinc-300">{l.message}</p>}
    {l.key_photos?.length > 0 && (
      <div data-testid={`lead-photos-${l.id}`} className="mt-3 flex flex-wrap gap-2 border-t border-white/10 pt-3">
        {l.key_photos.map((p, i) => (
          <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid={`lead-photo-${l.id}-${i}`}><img src={fileUrl(p)} alt="Key" className="h-20 w-20 border border-white/15 object-cover" /></a>
        ))}
      </div>
    )}
  </div>
);

export const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = filter === "all" ? leads : leads.filter((l) => l.kind === filter);
  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`leads-filter-${f}`} onClick={() => setFilter(f)} className={`border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] ${filter === f ? "border-brand text-brand" : "border-white/10 text-zinc-400"}`}>{f === "quote" ? "Quotes" : f === "contact" ? "Messages" : "All"}</button>
          ))}
        </div>
        <span data-testid="leads-new-count" className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{newCount} new · {leads.length} total</span>
      </div>
      <div className="mt-6 space-y-3">
        {shown.map((l) => <LeadCard key={l.id} l={l} onStatus={onStatus} onDelete={onDelete} />)}
        {!shown.length && <p data-testid="leads-empty" className="border border-dashed border-white/15 p-10 text-center text-sm text-zinc-400">No requests yet. New quotes and messages from the website will appear here.</p>}
      </div>
    </div>
  );
};
